import React from "react";
import { Link } from "react-router-dom";
import SectionLabel from "@/components/SectionLabel";
import Reveal from "@/components/Reveal";

const CHAPTERS = [
  {
    years: "2015 — 2022",
    title: "The high-functioning breakdown.",
    body: [
      "Since 2015, Andrew had been quietly battling chronic depression and an undiagnosed mood disorder while scaling his first company, LiftEd, alongside his co-founder and sister. From the outside: founder, builder, operator. From the inside: a nervous system running on its reserves, every single day.",
      "LiftEd sold in June 2022. He became a new father the same year.",
    ],
  },
  {
    years: "2023",
    title: "The diagnosis that explained a decade.",
    body: [
      "In 2023, Andrew received a severe ADHD diagnosis — the root cause of nearly two decades of what had looked like burnout, moodiness, and inconsistency. What had been framed as a character flaw was a neurological condition he'd been self-medicating around his entire adult life.",
      "He took a high-paying role at Tidal as a Senior Product Manager, leading new music tools for independent artists. On paper, another win. Underneath, a father, husband, and operator trying to out-run his own biology.",
    ],
  },
  {
    years: "Late 2024",
    title: "30 days. No prescriptions.",
    body: [
      "He checked into a residential mental health treatment center for thirty days. Came off every prescription he had been carrying. For the first time since the beginning, learned — actually learned — how to manage ADHD, not mask it.",
      "By that point he had already spent nearly two decades experimenting with every lever he could find: nootropics, supplements, antidepressants, stimulants. He knew what helped. He knew what hurt. He knew what the market was selling, and what the market was lying about.",
    ],
  },
  {
    years: "Early 2025 — Today",
    title: "A commitment, and a product.",
    body: [
      "He made a commitment in early 2025: never again sacrifice his mental health to perform. And if he was going to build again, it would be for ambitious people like himself — those who want to perform at a high level while staying grounded, clear, and well.",
      "Vitura was born from that mission. A daily performance shot engineered around the ingredients that actually worked, at the clinical doses the research actually used, with zero of the things that didn't earn their place in the formula. Built by someone who had to stop performing to learn how to perform again.",
    ],
  },
];

export default function Story() {
  return (
    <div className="max-w-site mx-auto px-6 md:px-10 py-16 md:py-24" data-testid="story-page">
      {/* HERO */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-6">
          <SectionLabel index="01 / Founding Story">An obsession with label transparency starts with a story.</SectionLabel>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-[-0.03em] leading-[0.95] mt-6">
            Built by someone who had to <span className="editorial normal-case">stop performing</span> to learn how to perform again.
          </h1>
          <p className="mt-8 editorial text-xl md:text-2xl leading-snug text-[color:var(--ink-2)] max-w-xl">
            For the ambitious. For the ones who've been scaling companies, families, and nervous systems all at the same time.
          </p>
        </div>
        <div className="lg:col-span-6">
          <div className="relative aspect-[9/16] max-h-[720px] border border-[color:var(--line)] bg-[#0f0f10] overflow-hidden" data-testid="story-founder-reel">
            <iframe
              src="https://www.instagram.com/reel/DJDTfOdM_LK/embed/"
              title="Andrew Hill's Vitura journey"
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              scrolling="no"
              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
          <div className="mt-3 flex items-center justify-between">
            <div>
              <div className="label !text-[10px]">FIG. 001 · Founder</div>
              <div className="text-sm mt-1">Andrew Hill — on the journey that led to Vitura</div>
            </div>
            <a
              href="https://www.instagram.com/reel/DJDTfOdM_LK/"
              target="_blank"
              rel="noopener noreferrer"
              className="label underline hover:opacity-60 transition"
              data-testid="story-founder-reel-link"
            >
              Watch on Instagram →
            </a>
          </div>
        </div>
      </div>

      {/* CHAPTERS / TIMELINE */}
      <section className="mt-24 md:mt-32" data-testid="story-chapters">
        <SectionLabel index="02 / A Decade">In four chapters.</SectionLabel>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-12 border-t border-[color:var(--line)]">
            {CHAPTERS.map((ch, i) => (
              <Reveal key={ch.years} delay={i * 0.05}>
                <article className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 py-12 md:py-16 border-b border-[color:var(--line)]" data-testid={`story-chapter-${i}`}>
                  <div className="lg:col-span-3">
                    <div className="label !text-[10px] text-[color:var(--ink-3)]">Chapter {String(i + 1).padStart(2, "0")}</div>
                    <div className="font-mono tabular-nums text-sm mt-2 text-[color:var(--ink-2)]">{ch.years}</div>
                  </div>
                  <div className="lg:col-span-9">
                    <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.05]">
                      {ch.title}
                    </h2>
                    <div className="mt-6 space-y-4 text-[16px] leading-[1.75] text-[color:var(--ink-2)] max-w-3xl">
                      {ch.body.map((p, pi) => (<p key={pi}>{p}</p>))}
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section className="mt-20 md:mt-28 grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="story-quote">
        <div className="lg:col-span-2 flex lg:justify-end">
          <span className="editorial text-6xl md:text-8xl text-[color:var(--ink)] leading-none">&ldquo;</span>
        </div>
        <div className="lg:col-span-10">
          <blockquote className="editorial text-3xl md:text-5xl leading-[1.1] text-[color:var(--ink)] max-w-3xl">
            I had to hit rock bottom to deeply appreciate how important my mental health is. I'm not willing to sacrifice it again to perform. So I built what I wish I'd had the whole time.
          </blockquote>
          <div className="mt-8 flex items-center gap-4">
            <span className="w-10 h-px bg-[color:var(--ink)]/40" />
            <div className="label">Andrew Hill · Founder, Vitura · <a href="https://instagram.com/livewelldru" target="_blank" rel="noopener noreferrer" className="hover:opacity-60 transition" data-testid="story-quote-ig">@livewelldru</a></div>
          </div>
        </div>
      </section>

      {/* NAME / ETYMOLOGY */}
      <section className="mt-24 md:mt-32 border-t border-[color:var(--line)] pt-16" data-testid="story-name">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <SectionLabel index="03 / The Name">Vitura.</SectionLabel>
          </div>
          <div className="lg:col-span-8 space-y-5 text-[16px] leading-[1.75] text-[color:var(--ink-2)] max-w-3xl">
            <p>
              The brand was first conceived as <span className="editorial text-[color:var(--ink)]">Auvem</span> — a variant on the Latin word <span className="editorial text-[color:var(--ink)]">aevum</span>, meaning lifetime, eternity, age. A name for the long arc of a life well-performed.
            </p>
            <p>
              It became <span className="editorial text-[color:var(--ink)]">Vitura</span>. The shape of a daily ritual inside that long arc. A 2oz shot you can repeat for a lifetime of ambitious mornings, grounded afternoons, and earned recoveries.
            </p>
            <p className="editorial text-2xl text-[color:var(--ink)] pt-2">
              Built for the lifetime, not just the launch.
            </p>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-3 border border-[color:var(--line)]" data-testid="story-stats">
        {[
          { n: "15", l: "Clinical Actives" },
          { n: "8+", l: "Branded Ingredients" },
          { n: "3P", l: "EUROFINS Verified" },
        ].map((s, i) => (
          <Reveal key={s.l} delay={i * 0.05} className="border-b last:border-b-0 md:border-b-0 md:border-r md:last:border-r-0 border-[color:var(--line)]">
            <div className="p-10">
              <div className="label !text-[10px]">No. 0{i + 1}</div>
              <div className="font-display text-6xl md:text-7xl tracking-[-0.04em] mt-4">{s.n}</div>
              <div className="label !text-[10px] mt-3">{s.l}</div>
            </div>
          </Reveal>
        ))}
      </section>

      {/* CROSS-SELL */}
      <section className="mt-20 md:mt-28" data-testid="story-crosssell">
        <Link to="/journal" className="block border border-[color:var(--line)] p-8 hover:bg-[color:var(--bg-soft)] transition group max-w-2xl mx-auto text-center" data-testid="story-cta-journal">
          <div className="label !text-[10px]">Journal</div>
          <div className="font-display text-3xl mt-2">Read the science →</div>
          <p className="text-sm text-[color:var(--ink-2)] mt-3">Longform pieces on why we formulated the way we did.</p>
        </Link>
      </section>
    </div>
  );
}
